/**
 * Shared state for the AI-input preset affordances. Both `StepAIInput.tsx` and
 * `StepAIInput.web.tsx` consume this, so the two platform files stay pure
 * rendering and can never drift on when the sheet opens or what "applied"
 * means.
 *
 * The sheet's catalog is the unified preset + real-skill row list (see
 * `cv-preset-rows.ts`): the 5 curated presets merged with every catalog CV
 * skill, fetched here so a pick can apply the actual SKILL.md server-side.
 * The catalog query degrades silently: with no skills loaded, the rows are
 * exactly the 5 presets (today's behaviour).
 */
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { prettySkillName } from '../skills/skill-name';
import { useSkillsInfinite } from '../skills/use-skills-query';
import { useOnboarding } from './OnboardingProvider';
import {
  buildCvPresetRows,
  filterCvPresetRows,
  parseSkillStyleId,
  type CvPresetRow,
  type CvPresetRowEntry,
} from './cv-preset-rows';

export type { CvPresetRow, CvPresetRowEntry } from './cv-preset-rows';

export function useCvPreset() {
  const { t, i18n } = useTranslation('auth');
  const { cvPresetId, applyCvPreset, clearCvPreset, isApplyingCvPreset } =
    useOnboarding();
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  // The catalog behind the sheet. Shares the skills cache with the rest of
  // the app; onboarding users are authenticated, so the explore shelf loads.
  const skillsQuery = useSkillsInfinite({ shelf: 'explore', type: 'cv' });
  const skills = useMemo(
    () => skillsQuery.data?.pages.flatMap((page) => page.skills) ?? [],
    [skillsQuery.data],
  );
  const isArabic = i18n.language?.startsWith('ar') ?? false;

  const rowEntries = useMemo<CvPresetRowEntry[]>(() => {
    const rows = buildCvPresetRows(skills);
    return rows.map((row) => {
      if (row.kind === 'skill') {
        const { skill } = row;
        return {
          row,
          label: prettySkillName(
            (isArabic && skill.nameAr) || skill.name || skill.skillId,
          ),
          description:
            (isArabic && skill.descriptionAr) || skill.description || '',
        };
      }
      return {
        row,
        label: t(`onboarding.cvPreset.presets.${row.preset.id}.label`, {
          defaultValue: row.preset.id,
        }),
        description: t(`onboarding.cvPreset.presets.${row.preset.id}.desc`, {
          defaultValue: '',
        }),
      };
    });
  }, [skills, t, isArabic]);

  const openSheet = useCallback(() => setIsSheetOpen(true), []);
  const closeSheet = useCallback(() => setIsSheetOpen(false), []);

  const apply = useCallback(
    async (row: CvPresetRow) => {
      await applyCvPreset(row.id, row.layout);
      setIsSheetOpen(false);
    },
    [applyCvPreset],
  );

  const appliedEntry =
    rowEntries.find((entry) => entry.row.id === cvPresetId) ?? null;
  // A resumed skill pick may predate the catalog load: fall back to a
  // readable name derived from the skill id so the chip never shows nothing.
  const appliedSkillId = parseSkillStyleId(cvPresetId);
  const appliedLabel = cvPresetId
    ? (appliedEntry?.label ??
      (appliedSkillId ? prettySkillName(appliedSkillId) : null))
    : null;

  return {
    isSheetOpen,
    openSheet,
    closeSheet,
    appliedPresetId: cvPresetId,
    appliedLabel,
    rowEntries,
    filterRows: filterCvPresetRows,
    isLoadingSkills: skillsQuery.isLoading,
    apply,
    clear: clearCvPreset,
    isApplying: isApplyingCvPreset,
  };
}
